// @flow
import React, { Children, useContext, useEffect, useRef, type Node } from 'react';
import classnames from 'classnames';
import { forceCheck } from 'react-lazyload';
import { throttle } from 'lodash';
import { FocusOn } from 'react-focus-on';

import { COLORS } from 'Components/utils/color/colors';
import { ToastContext } from 'Context/ToastContext';

import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker.jsx';
import Portal from 'Components/frameworks/portal/Portal.jsx';
import { LayerHeader } from './layer-header/LayerHeader.jsx';
import { type LayerHeaderButtonProps } from './layer-header/layer-header-button/LayerHeaderButton.jsx';

type Props = {
  ariaLabel?: string,
  ariaLabelledby?: string,
  bgColor?: AppGrayColors | COLORS.WHITE,
  buttonLeft?: ?LayerHeaderButtonProps,
  buttonRight?: ?LayerHeaderButtonProps,
  children: Node,
  className?: string,
  closeLabel?: string,
  dataTestId?: string,
  enterAnimation?: 'slideInBottom' | 'slideInRight',
  externalFocusTrapRefs?: Array<{ current: ?HTMLElement }>,
  hasAnimateEnter?: boolean,
  hasContainer?: boolean,
  hasContainerHeader?: boolean,
  hasNoFocusTrap?: boolean,
  hasScrollLocked?: boolean,
  header?: Node,
  onClose?: (e?: SyntheticEvent<>) => void,
  portalName?: string,
  theme?: string,
};

const HEIGHT = 60;

const onScroll = throttle(() => {
  forceCheck();
}, 250);

export function Layer(props: Props) {
  const { ref } = useContext(ToastContext);

  const {
    ariaLabel,
    ariaLabelledby,
    bgColor = COLORS.GRAY_LIGHTER,
    buttonLeft,
    buttonRight,
    children,
    className,
    closeLabel,
    dataTestId = 'Layer',
    enterAnimation = 'slideInRight',
    externalFocusTrapRefs = [],
    hasAnimateEnter,
    hasContainer,
    hasContainerHeader,
    hasNoFocusTrap,
    hasScrollLocked,
    header,
    onClose,
    portalName,
    theme = 'default',
  } = props;

  const layerBody: { current: ?HTMLElement } = useRef();

  useEffect(() => {
    layerBody.current && layerBody.current.addEventListener('scroll', onScroll);

    return () => {
      layerBody.current && layerBody.current.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (!Children.count(children)) {
    return null;
  }

  const hasHeader = buttonLeft || buttonRight || header;

  const shards = ref ? [ref, ...externalFocusTrapRefs] : externalFocusTrapRefs;

  return (
    <Portal name={portalName}>
      <FocusOn allowPinchZoom enabled={!hasNoFocusTrap} onEscapeKey={onClose} shards={shards}>
        <BodyLocker isLocked>
          <aside
            aria-label={ariaLabelledby ? null : ariaLabel}
            aria-labelledby={ariaLabelledby}
            role="dialog"
          >
            <div
              className={classnames(
                className,
                'fixed inset-0 w-full escape-responsive-helper',
                `bg-${bgColor}`,
                {
                  // $FlowFixMe
                  [enterAnimation]: hasAnimateEnter,
                },
              )}
              data-testid={dataTestId}
              role="document"
            >
              {hasHeader && (
                <LayerHeader
                  buttonLeft={buttonLeft}
                  buttonRight={buttonRight}
                  closeLabel={[closeLabel, ariaLabel].join(' ').trim()}
                  hasContainer={hasContainerHeader}
                  height={HEIGHT}
                  onClose={onClose}
                  theme={theme}
                >
                  {header}
                </LayerHeader>
              )}
              <div
                className={`h-full z-0 transition-all${
                  hasScrollLocked ? ' overflow-hidden' : ' scroll-y'
                }${hasContainer ? ' px-5' : ''}`}
                data-testid="Layer-body"
                ref={layerBody}
                style={hasHeader ? { paddingTop: HEIGHT } : {}}
              >
                <div
                  className={classnames('h-full min-h-full', {
                    'mx-auto max-w-site w-full': hasContainer,
                  })}
                  data-testid="Layer-content"
                >
                  {children}
                </div>
              </div>
            </div>
          </aside>
        </BodyLocker>
      </FocusOn>
    </Portal>
  );
}
